import React, { FC, useMemo, useState } from 'react';
import { BrandLogo, Icon } from '@kontentino/ui';
import { ListPost } from 'types/PostDetail';
import PostTypeIcon from 'app/modules/posts/components/PostTypeIcon';
import { getPseudoTypeByPost } from 'utils/post';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { PseudoPostType } from 'constants/post';
import { isPostGlobal } from 'app/modules/posts/utils/globalPosts';
import { getDateTimeLabel } from 'app/modules/posts/utils/getDateTimeLabel';
import { useTranslation } from 'react-i18next';
import StringUtils from 'utils/string';
import useOpenPost from 'app/modules/posts/hooks/useOpenPost';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons/faFilePdf';

type Props = {
  post: ListPost;
};

const TRUNCATED_TEXT_CHARACTERS = 250;

const ApprovalPostCard: FC<Props> = ({ post }) => {
  const { t } = useTranslation();
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const openPost = useOpenPost();

  const pseudoPostType = useMemo(
    () =>
      getPseudoTypeByPost({
        type: post.type,
        subtype: post.subtype,
      }),
    [post.type, post.subtype],
  );
  const dateTimeLabel = getDateTimeLabel(post.date, post.time);
  return (
    <div
      className="tw-group tw-relative tw-w-full tw-cursor-pointer tw-overflow-hidden md:tw-max-w-[460px]"
      data-name="approvals-post-card"
      data-cy="approvals_post-card"
      data-post-id={post.id}
      onClick={() => openPost(post)}
    >
      <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-shadow-sm">
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3 tw-p-4">
          <BrandLogo
            src={post.page.logo?.src}
            name={post.page.name}
            size={40}
            socialMediaType={getSocialMediaTypeForBrandLogo(post.page.type)}
            iconSize={18}
          />

          <div className="tw-flex tw-flex-1 tw-flex-col">
            <div className="tw-flex tw-items-center tw-gap-1">
              <span className="tw-font-semibold tw-text-grayscale-180">
                {post.page.name}
              </span>
              {isPostGlobal(post) && (
                <Icon
                  icon={faGlobe}
                  className="tw-text-grayscale-100"
                  size="sm"
                />
              )}
            </div>
            {dateTimeLabel.length > 0 && (
              <span className="tw-text-sm tw-text-grayscale-100">
                {dateTimeLabel}
              </span>
            )}
          </div>
        </div>

        <div className="tw-shrink-0 tw-px-4 tw-pb-4">
          {(!post.text || post.text.length === 0) &&
            !post.preview_image?.src &&
            pseudoPostType !== PseudoPostType.Document && (
              <p className="tw-whitespace-pre-wrap tw-text-md !tw-text-grayscale-50">
                {t('widgets.emptyMessage.postContent')}
              </p>
            )}
          {post.text && (
            <p className="tw-whitespace-pre-wrap tw-text-md tw-text-grayscale-180">
              {isTextExpanded
                ? post.text
                : StringUtils.truncate(post.text, TRUNCATED_TEXT_CHARACTERS)}
              {post.text.length > TRUNCATED_TEXT_CHARACTERS && (
                <span
                  className="tw-cursor-pointer tw-font-medium tw-transition-opacity hover:tw-opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsTextExpanded(!isTextExpanded);
                  }}
                  data-name="approval-post-card-text_toggle-button"
                  data-cy="approval-post-card-text_toggle-button"
                >
                  {isTextExpanded ? ` ${t('showLess')}` : ` ${t('showMore')}`}
                </span>
              )}
            </p>
          )}
        </div>

        {pseudoPostType === PseudoPostType.Document ? (
          <div className="tw-relative tw-flex tw-h-[259px] tw-w-full tw-shrink-0 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-bg-grayscale-5">
            <Icon icon={faFilePdf} className="tw-text-grayscale-70" size={48} />
            <div className="tw-absolute tw-bottom-2 tw-right-2 tw-flex tw-rounded-sm tw-bg-white tw-px-1 tw-py-[2px]">
              <PostTypeIcon type={pseudoPostType} />
            </div>
          </div>
        ) : (
          post.preview_image?.src && (
            <div className="tw-relative tw-h-[259px] tw-w-full tw-shrink-0">
              <img
                src={post.preview_image.src}
                alt="Post preview"
                loading="lazy"
                decoding="async"
                className="tw-h-full tw-w-full tw-object-cover"
              />
              {pseudoPostType && (
                <div className="tw-absolute tw-bottom-2 tw-right-2 tw-flex tw-rounded-sm tw-bg-white tw-px-1 tw-py-[2px]">
                  <PostTypeIcon type={pseudoPostType} />
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ApprovalPostCard;
